import { HTTPError } from 'ky';
import { z } from 'zod';
import { apiFor } from './client';
import { AUTH_BASE_URL } from '@/features/dashboard/config';

/*
 * Admin user-management API. These endpoints live on the central registry
 * (the same server that issues logins — AUTH_BASE_URL) and are gated by a
 * bearer JWT with role=admin. apiFor() attaches the signed-in admin's token,
 * so no extra auth wiring is needed here.
 *
 * Only IceMobile staff (role=admin) ever reach this surface; client-role users
 * are blocked at the route guard AND by the backend (403).
 */

const adminApi = () => apiFor(AUTH_BASE_URL);

export const adminUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  client_id: z.string(),
  role: z.enum(['client', 'admin']),
  active: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
  last_login_at: z.string().nullable().optional(),
  allowed_reports: z.array(z.string()).default([]),
});

export type AdminUser = z.infer<typeof adminUserSchema>;

const listResponseSchema = z.object({ data: z.array(adminUserSchema) });
const itemResponseSchema = z.object({ data: adminUserSchema });

export type CreateUserInput = {
  email: string;
  client_id: string;
  password: string;
  role: 'client' | 'admin';
  allowed_reports: string[];
};

// Every field optional — the backend PATCH only touches what's supplied. Omit
// `password` to leave the current one untouched; omit `allowed_reports` to keep
// existing grants (send [] to revoke all).
export type UpdateUserInput = {
  email?: string;
  client_id?: string;
  role?: 'client' | 'admin';
  active?: boolean;
  password?: string;
  allowed_reports?: string[];
};

/** Error carrying the backend's human message (from `{ error: { message } }`). */
export class AdminApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'AdminApiError';
  }
}

// Turn a ky HTTPError into an AdminApiError with the server's message. The API
// error shape is `{ error: { message, code } }`; fall back to the status text
// if the body can't be read.
async function toAdminError(err: unknown): Promise<AdminApiError> {
  if (err instanceof HTTPError) {
    let message = err.response.statusText || 'Request failed';
    try {
      const body = (await err.response.json()) as { error?: { message?: string } };
      if (body?.error?.message) message = body.error.message;
    } catch {
      /* non-JSON body — keep the status text */
    }
    return new AdminApiError(message, err.response.status);
  }
  return new AdminApiError('Could not reach the server', 0);
}

export async function listUsers(): Promise<AdminUser[]> {
  try {
    const raw = await adminApi().get('admin/users').json();
    return listResponseSchema.parse(raw).data;
  } catch (err) {
    throw await toAdminError(err);
  }
}

export async function createUser(input: CreateUserInput): Promise<AdminUser> {
  try {
    const raw = await adminApi().post('admin/users', { json: input }).json();
    return itemResponseSchema.parse(raw).data;
  } catch (err) {
    throw await toAdminError(err);
  }
}

export async function updateUser(id: string, patch: UpdateUserInput): Promise<AdminUser> {
  try {
    const raw = await adminApi().patch(`admin/users/${id}`, { json: patch }).json();
    return itemResponseSchema.parse(raw).data;
  } catch (err) {
    throw await toAdminError(err);
  }
}
